import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { ApiError, errorMessage } from '../api/client';
import {
  billNumberOf,
  isSellable,
  repricedMrpOf,
  posApi,
  saleTotalOf,
  stockCountOf,
  stockLabelOf,
  type CounterCouponCheck,
  type PosBook,
  type PosSale,
} from '../api/pos';
import { money } from '../format';
import InvoiceView from '../components/InvoiceView';
import ConfirmSale from '../components/ConfirmSale';
import TenderPicker, {
  EMPTY_TENDER,
  tenderLabel,
  tenderPayload,
  tenderProblem,
  type TenderValue,
} from '../components/TenderPicker';
import CustomerDetails, {
  EMPTY_CUSTOMER,
  customerPayload,
  customerPhoneOf,
  customerProblem,
  type CustomerDetailsValue,
} from '../components/CustomerDetails';

interface Line {
  book: PosBook;
  quantity: number;
}

/** Long enough not to fire on every keystroke, short enough to feel immediate. */
const SEARCH_DEBOUNCE_MS = 180;

/** How long the bill has to sit still before an applied coupon is re-checked. */
const COUPON_RECHECK_MS = 300;

/** The percentage discounts offered as one-press buttons beside the rupee box. */
const DISCOUNT_PERCENTS = [5, 10] as const;
type DiscountPercent = (typeof DISCOUNT_PERCENTS)[number];

export default function BillingScreen({
  active,
  resumed,
}: {
  active: boolean;
  /** Bumps when maintenance ends — the cue to take the scanner's focus back. */
  resumed: number;
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<PosBook[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  // Which query `results` actually belongs to. Without this, a fast second scan
  // could be matched against the previous book's results and bill the wrong
  // title — the single most expensive mistake this screen could make.
  const [resultsFor, setResultsFor] = useState('');

  const [lines, setLines] = useState<Line[]>([]);
  // Held as the text staff typed, not as a number. A discount is typed left to
  // right — "1", "15", "150" — and a numeric state would have to decide what
  // an empty box or a lone "." means on every keystroke. The number is derived
  // once, below, and that is the only place the two can disagree.
  const [discountText, setDiscountText] = useState('');
  // A percentage picked on the buttons instead of rupees typed in the box.
  // Held as the percentage, not as the rupees it came to, so it keeps up with
  // books scanned on or taken off after it was picked.
  const [discountPercent, setDiscountPercent] = useState<DiscountPercent | null>(null);
  // A coupon the customer brought. `couponText` is the box; `couponCode` is
  // set once staff press Apply, and from then on the server is asked again
  // whenever the bill or the phone changes, because both change what the
  // coupon is worth or whether it can be used at all. `couponCheck` is the
  // last answer, tagged with the bill it was for so a stale one is never
  // billed.
  const [couponText, setCouponText] = useState('');
  const [couponCode, setCouponCode] = useState<string | null>(null);
  const [couponCheck, setCouponCheck] = useState<{
    key: string;
    result?: CounterCouponCheck;
    error?: string;
  } | null>(null);
  const [tender, setTender] = useState<TenderValue>(EMPTY_TENDER);
  const [tenderAsked, setTenderAsked] = useState(false);
  // These books are being carried to the other shop, not sold. The bill is
  // rung up the same way so the shelf count drops, but nothing is tendered.
  const [isTransfer, setIsTransfer] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saleError, setSaleError] = useState<string | null>(null);
  const [lastSale, setLastSale] = useState<PosSale | null>(null);
  // The just-completed bill, shown over the (already emptied) till so staff
  // can print it, save it, or close it for this customer.
  const [invoice, setInvoice] = useState<PosSale | null>(null);
  // The last look before the sale is banked, so a customer who changes their
  // mind on hearing the total costs a click rather than an admin void.
  const [confirming, setConfirming] = useState(false);

  const [customer, setCustomer] = useState<CustomerDetailsValue>(EMPTY_CUSTOMER);
  // Folded away to start with: staff scan first and ask for the details
  // while the bill is being totted up. Completing the sale without
  // them opens it back up rather than letting it slip past.
  const [customerOpen, setCustomerOpen] = useState(false);
  // Only set once staff have actually tried to complete the sale — a
  // panel that turns red while they are still typing the first letter of a
  // name reads as a fault rather than a prompt.
  const [customerAsked, setCustomerAsked] = useState(false);

  const [resolvingScan, setResolvingScan] = useState(false);

  // Which result the arrow keys have moved to, or -1 for none. Every scan
  // leaves it at -1, and that is what tells Enter apart: nothing chosen means
  // the term is a barcode to resolve, a choice means staff have read the list
  // and picked off it.
  const [activeIndex, setActiveIndex] = useState(-1);

  const searchRef = useRef<HTMLInputElement>(null);
  const customerPhoneRef = useRef<HTMLInputElement>(null);
  const customerNameRef = useRef<HTMLInputElement>(null);
  // Guards against a slow early response overwriting a newer one.
  const requestSeq = useRef(0);
  // Mirrors `invoice` so the focus rule below can be read synchronously —
  // completeSale opens the bill and then runs its `finally` in the same tick,
  // where the state variable is still the previous render's value.
  const invoiceOpen = useRef(false);
  // Same reason as `invoiceOpen`: a scan must not slip past the confirmation
  // dialog into the bill behind it.
  const confirmOpen = useRef(false);

  // Never pull focus out of the open bill: the scanner types wherever focus
  // is, and a scan landing in the search box behind the dialog would start
  // building the next customer's bill unseen.
  const focusSearch = useCallback(() => {
    if (active && !invoiceOpen.current && !confirmOpen.current) searchRef.current?.focus();
  }, [active]);

  const openInvoice = useCallback((sale: PosSale) => {
    invoiceOpen.current = true;
    setInvoice(sale);
  }, []);

  const openConfirm = useCallback(() => {
    confirmOpen.current = true;
    setConfirming(true);
  }, []);

  const closeConfirm = useCallback(() => {
    confirmOpen.current = false;
    setConfirming(false);
    focusSearch();
  }, [focusSearch]);

  const closeInvoice = useCallback(() => {
    invoiceOpen.current = false;
    setInvoice(null);
    focusSearch();
  }, [focusSearch]);

  // The scanner types into whatever holds focus, so the search box has to be
  // holding it whenever staff are between actions. Anything that steals
  // focus (a button press, completing a sale) hands it straight back.
  useEffect(() => {
    focusSearch();
  }, [focusSearch, lines.length, lastSale, resumed]);

  // A new list is a new question: nothing is chosen on it until staff say so.
  //
  // Keyed on the typed term as well as the list, and the term is the half that
  // matters. Results lag what has been typed by the debounce, so on the term
  // alone the highlight would go on pointing into the PREVIOUS list for as
  // long as it takes the next one to arrive — and an Enter inside that window
  // would bill a book from a search staff have already moved on from.
  useEffect(() => {
    setActiveIndex(-1);
  }, [results, query]);

  // Keep the highlighted row on screen. `nearest` scrolls whichever ancestor
  // actually scrolls and does nothing when the row is already visible, so
  // arrowing through a short list never jumps the pane about.
  useEffect(() => {
    if (activeIndex < 0) return;
    document
      .getElementById(`scan-result-${activeIndex}`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      setResultsFor('');
      setSearching(false);
      setSearchError(null);
      return;
    }

    const controller = new AbortController();
    const seq = ++requestSeq.current;
    setSearching(true);

    const timer = window.setTimeout(() => {
      posApi
        .lookup(q, controller.signal)
        .then((books) => {
          if (seq !== requestSeq.current) return;
          setResults(books ?? []);
          setResultsFor(q);
          setSearchError(null);
        })
        .catch((err: unknown) => {
          if (controller.signal.aborted || seq !== requestSeq.current) return;
          setResults([]);
          setSearchError(errorMessage(err, 'Search failed.'));
        })
        .finally(() => {
          if (seq === requestSeq.current) setSearching(false);
        });
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [query]);

  const subtotal = useMemo(
    () => lines.reduce((sum, l) => sum + Number(l.book.price) * l.quantity, 0),
    [lines],
  );
  const itemCount = useMemo(() => lines.reduce((sum, l) => sum + l.quantity, 0), [lines]);

  // Taken at face value, never clamped. A discount that does not fit is an
  // error to be shown, not a smaller number to be billed quietly: rounding a
  // mistyped 1000 down to the 200 the bill came to is how a book once left the
  // shop for nothing. The quantity boxes above clamp because a shelf cannot
  // hand over more copies than it has; money has no such stop, so this one has
  // to be said out loud instead.
  //
  // A coupon comes off first, at the server's figure; staff's own discount
  // then comes off what is left, so 10% after a coupon is 10% of the rest.
  const couponPhone = customerPhoneOf(customer);
  const couponKey =
    couponCode && !isTransfer && lines.length
      ? JSON.stringify([
          couponCode,
          lines.map((l) => [l.book.id, l.quantity]),
          couponPhone ?? '',
        ])
      : null;
  const couponAnswer = couponCheck && couponCheck.key === couponKey ? couponCheck : null;
  const couponDiscount = couponAnswer?.result?.discount ?? 0;
  /** Why the coupon cannot go on this bill, in the words staff should read. */
  const couponProblem = !couponKey
    ? null
    : !couponPhone
      ? customer.declined
        ? 'A coupon needs the customer’s mobile number — untick “prefers not to share” and enter it.'
        : 'Enter the customer’s mobile number below to use the coupon. Each customer’s uses are counted by it.'
      : (couponAnswer?.error ?? null);
  // Asked and not yet answered — the bill waits rather than going out at a
  // figure the server has not agreed.
  const couponPending = !!couponKey && !!couponPhone && !couponAnswer;

  useEffect(() => {
    if (!couponKey || !couponCode || !couponPhone) return;
    let cancelled = false;
    const timer = window.setTimeout(() => {
      posApi
        .checkCoupon({
          code: couponCode,
          items: lines.map((l) => ({ bookId: l.book.id, quantity: l.quantity })),
          customerPhone: couponPhone,
        })
        .then((result) => {
          if (!cancelled) setCouponCheck({ key: couponKey, result });
        })
        .catch((err) => {
          if (!cancelled) {
            setCouponCheck({
              key: couponKey,
              error: errorMessage(err, 'The coupon could not be checked. Try again.'),
            });
          }
        });
    }, COUPON_RECHECK_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
    // `couponKey` already says everything the request depends on.
  }, [couponKey]);

  const afterCoupon = subtotal - couponDiscount;
  const typedDiscount = Number(discountText || 0);
  const discount =
    discountPercent !== null
      ? Math.round(afterCoupon * discountPercent) / 100
      : Number.isFinite(typedDiscount) && typedDiscount > 0
        ? typedDiscount
        : 0;

  /** Why this discount cannot be billed, in the words staff should read. */
  const discountProblem =
    discount > 0 && lines.length
      ? discount > afterCoupon
        ? `That is more than the bill comes to (${money(afterCoupon)}).`
        : discount === afterCoupon
          ? 'That leaves nothing to pay. A bill has to come to something — give a little less, or take the books off the bill instead.'
          : null
      : null;

  // Nothing that cannot be billed is allowed to move the total. Staff see the
  // bill as it stands and the reason the discount has not been taken off it,
  // rather than a figure the sale would be refused at.
  // A transfer carries no money at all: no discount to apply and nothing to
  // pay. `subtotal` still stands, because what left the shelf is still worth
  // something and the report says so.
  const discountApplied = isTransfer || discountProblem ? 0 : discount;
  const total = isTransfer ? 0 : afterCoupon - discountApplied;
  /** Everything off the bill, coupon and counter discount together. */
  const totalOff = isTransfer ? 0 : couponDiscount + discountApplied;

  function applyCoupon() {
    const code = couponText.trim().toUpperCase();
    if (!code) return;
    setSaleError(null);
    setCouponCheck(null);
    setCouponCode(code);
    setCouponText(code);
  }

  function removeCoupon() {
    setCouponCode(null);
    setCouponCheck(null);
    setCouponText('');
    setSaleError(null);
  }

  const quantityOnBill = useCallback(
    (bookId: string) => lines.find((l) => l.book.id === bookId)?.quantity ?? 0,
    [lines],
  );

  const addBook = useCallback(
    (book: PosBook) => {
      setSaleError(null);
      setLastSale(null);

      if (!isSellable(book)) {
        // Nothing to point staff at any more. The counter and the website
        // sell the same copies, so out of stock here means out of stock
        // everywhere: there is no stockroom to fetch from and nothing to move
        // across. The old message told staff to move stock to the store,
        // which would now send them to a screen that does nothing.
        setSaleError(`"${book.title}" is out of stock.`);
        return;
      }
      // Stop staff building a bill the server is bound to reject. The server
      // checks again on save — this is only to fail early and politely.
      const count = stockCountOf(book);
      if (count !== null && quantityOnBill(book.id) >= count) {
        setSaleError(
          `Only ${count} copy(s) of "${book.title}" left — all of them are already on this bill.`,
        );
        return;
      }

      setLines((prev) => {
        const idx = prev.findIndex((l) => l.book.id === book.id);
        if (idx === -1) return [...prev, { book, quantity: 1 }];
        const next = [...prev];
        next[idx] = { ...next[idx], quantity: next[idx].quantity + 1 };
        return next;
      });

      setQuery('');
      setResults([]);
      focusSearch();
    },
    [focusSearch, quantityOnBill],
  );

  /** The one book a scanned code can unambiguously mean, or null. */
  function resolveScan(books: PosBook[], term: string): PosBook | null {
    const q = term.toLowerCase();
    const exact = books.filter(
      (b) =>
        b.isbn13?.toLowerCase() === q ||
        b.sku?.toLowerCase() === q ||
        b.id.toLowerCase() === q,
    );
    if (exact.length === 1) return exact[0];
    // A used copy shares the new book's ISBN, so one barcode can be two books.
    // When only one of them is on the shelf that is the one in hand; when both
    // are, the list stays open with the USED tag for staff to pick.
    if (exact.length > 1) {
      const onShelf = exact.filter(isSellable);
      if (onShelf.length === 1) return onShelf[0];
    }
    // A lone result for a typed fragment is unambiguous too. More than one and
    // we say nothing — guessing here bills the customer for the wrong book.
    if (exact.length === 0 && books.length === 1) return books[0];
    return null;
  }

  /**
   * Where an arrow key lands, skipping what cannot be sold.
   *
   * A sold-out row is a disabled button, so stopping on one would leave staff
   * a highlighted line that Enter then refuses to add. Above the first result
   * the highlight comes off altogether — that is the way back to plain typing
   * — and below the last it simply stays put rather than wrapping round to
   * the top, which at a counter reads as the list having jumped.
   */
  function stepActive(from: number, step: 1 | -1): number {
    let i = from + step;
    while (i >= 0 && i < results.length && !isSellable(results[i])) i += step;
    if (i < 0) return -1;
    if (i >= results.length) return from;
    return i;
  }

  /**
   * A barcode scanner types the code and sends Enter within a few milliseconds
   * — well inside the search debounce. So on Enter we cannot rely on `results`
   * having caught up: it is either empty or, worse, still holding the PREVIOUS
   * scan's matches. Only results known to belong to this exact term are used;
   * otherwise we fetch synchronously and add from that.
   */
  async function onSearchKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    // Down and up walk the results. preventDefault stops the caret jumping to
    // the ends of what has been typed, which would otherwise put the next
    // keystroke in the wrong place.
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      if (!results.length) return;
      e.preventDefault();
      const step = e.key === 'ArrowDown' ? 1 : -1;
      setActiveIndex((i) => stepActive(i, step));
      return;
    }

    if (e.key !== 'Enter') return;
    e.preventDefault();

    const term = query.trim();
    if (!term || resolvingScan) return;

    // A row staff have moved to beats anything the term might resolve to:
    // they have looked at the list and chosen, which is exactly the case the
    // scan path cannot settle on its own.
    const picked = activeIndex >= 0 ? results[activeIndex] : undefined;
    if (picked) {
      addBook(picked);
      return;
    }

    if (resultsFor === term) {
      const hit = resolveScan(results, term);
      if (hit) addBook(hit);
      // Results are current and ambiguous — let staff choose.
      return;
    }

    setResolvingScan(true);
    try {
      const books = await posApi.lookup(term);
      // Bump the sequence so the in-flight debounced request cannot overwrite
      // what we just resolved.
      requestSeq.current += 1;
      setResults(books ?? []);
      setResultsFor(term);
      setSearchError(null);
      const hit = resolveScan(books ?? [], term);
      if (hit) addBook(hit);
    } catch (err) {
      setSearchError(errorMessage(err, 'Search failed.'));
    } finally {
      setResolvingScan(false);
      setSearching(false);
    }
  }

  function setQuantity(bookId: string, quantity: number) {
    setSaleError(null);
    setLines((prev) =>
      prev.flatMap((l) => {
        if (l.book.id !== bookId) return [l];
        if (quantity <= 0) return [];
        // Cap at the known stock. When the count is hidden there is nothing to
        // cap against, so let it through — the server still refuses to oversell.
        const cap = stockCountOf(l.book) ?? Number.POSITIVE_INFINITY;
        return [{ ...l, quantity: Math.min(quantity, cap) }];
      }),
    );
  }

  function removeLine(bookId: string) {
    setSaleError(null);
    setLines((prev) => prev.filter((l) => l.book.id !== bookId));
    focusSearch();
  }

  function clearBill() {
    confirmOpen.current = false;
    setConfirming(false);
    setLines([]);
    setDiscountText('');
    setDiscountPercent(null);
    setCouponText('');
    setCouponCode(null);
    setCouponCheck(null);
    setSaleError(null);
    setLastSale(null);
    setQuery('');
    setResults([]);
    // Clearing the bill abandons this customer too, details and all.
    setCustomer(EMPTY_CUSTOMER);
    setCustomerOpen(false);
    setCustomerAsked(false);
    focusSearch();
  }

  /**
   * What the Complete sale button does: check the bill is fit to send, then
   * put it up for confirmation. The details are validated BEFORE the dialog
   * rather than after it, so staff are never sent back to the till by a
   * missing mobile number they had already agreed the total for.
   */
  function askToComplete() {
    if (!lines.length || saving || confirming) return;

    // Said here as well as under the field, because this is the moment staff
    // are trying to move past it.
    if (couponProblem) {
      setSaleError(`That coupon cannot be used. ${couponProblem}`);
      if (!couponPhone) setCustomerOpen(true);
      return;
    }
    if (couponPending) {
      setSaleError('Still checking the coupon — a moment.');
      return;
    }

    if (discountProblem) {
      setSaleError(`That discount cannot be given. ${discountProblem}`);
      return;
    }

    // A split has to add up to the bill before anything is sent.
    if (!isTransfer) {
      setTenderAsked(true);
      const split = tenderProblem(tender, total);
      if (split) {
        setSaleError(split);
        return;
      }
    }

    // The details are asked for on every bill. The only way past them is
    // staff ticking that the customer would rather not share — so a bill
    // with neither is a bill nobody asked, and that is what this stops.
    //
    // A transfer is the exception, and not a grudging one: there is no
    // customer at all, so there is nobody to ask and nothing to decline.
    // Making staff tick "prefers not to share" on behalf of the other branch
    // would be asking them to answer a question that was never put.
    if (!isTransfer) {
      setCustomerAsked(true);
      if (customerProblem(customer)) {
        setCustomerOpen(true);
        setSaleError(null);
        // The panel may have been collapsed; give it a frame to open before
        // asking for the focus inside it.
        // The number first, as the form asks it: it may fill the name in.
        const field = customerPhoneOf(customer) ? customerNameRef : customerPhoneRef;
        window.setTimeout(() => field.current?.focus(), 0);
        return;
      }
    }

    setSaleError(null);
    openConfirm();
  }

  async function completeSale() {
    if (!lines.length || saving) return;

    setSaving(true);
    setSaleError(null);
    try {
      const sale = await posApi.createSale({
        items: lines.map((l) => ({ bookId: l.book.id, quantity: l.quantity })),
        // A transfer sends neither a tender nor a discount — the server
        // refuses both on one — and an ordinary sale sends exactly what it
        // always did, with no new field on it at all.
        ...(isTransfer
          ? { isShopTransfer: true as const }
          : {
              ...tenderPayload(tender),
              // Left off entirely when there is none. The server prices the
              // bill itself and checks this against its own subtotal, so what
              // goes out is the discount staff agreed, not the total they
              // were shown.
              ...(discountApplied > 0
                ? { discount: Number(discountApplied.toFixed(2)) }
                : {}),
              // Checked again by the server when the bill is written; what
              // it takes off then is what the bill says.
              ...(couponCode && couponDiscount > 0 ? { couponCode } : {}),
            }),
        // Nothing about a customer travels on a transfer — there wasn't one.
        ...(isTransfer ? {} : customerPayload(customer)),
      });
      // Sale is banked — clear down for the next customer and put the bill
      // number where staff cannot miss it.
      setLastSale(sale);
      setLines([]);
      // Never carry one customer's discount onto the next person's bill, for
      // the same reason their name is not carried either.
      setDiscountText('');
      setDiscountPercent(null);
      setCouponText('');
      setCouponCode(null);
      setCouponCheck(null);
      setQuery('');
      setResults([]);
      setTender(EMPTY_TENDER);
      setTenderAsked(false);
      // Never carry a transfer onto the next customer's bill — the next thing
      // through the till is almost always an ordinary sale.
      setIsTransfer(false);
      // Never carry one customer's details onto the next person's bill.
      setCustomer(EMPTY_CUSTOMER);
      setCustomerOpen(false);
      setCustomerAsked(false);
      confirmOpen.current = false;
      setConfirming(false);
      // The till behind is already a fresh bill; this customer's receipt comes
      // up over it so printing is one click and not a trip to Today's bills.
      openInvoice(sale);
    } catch (err) {
      // The most important message in the app: the server rejects the sale when
      // the last copy sold online a moment ago. It names the book, so show it
      // verbatim rather than a generic failure.
      //
      // Maintenance is the exception. The server's text there is about the
      // outage, and the maintenance screen already says it; what staff need
      // under the bill once it lifts is that this sale did NOT go through.
      // The lines are left exactly as they were, so it is one more press.
      setSaleError(
        err instanceof ApiError && err.isMaintenance
          ? 'This sale was not saved — the till was closed for maintenance. Complete it again.'
          : errorMessage(err, 'The sale could not be completed.'),
      );
      // Back to the bill, where the error is readable and the lines can be
      // fixed — a failure message under a dialog nobody can act on is worse
      // than no message at all.
      confirmOpen.current = false;
      setConfirming(false);
    } finally {
      setSaving(false);
      focusSearch();
    }
  }

  return (
    <div className="billing">
      <section className="pane pane-search">
        <label className="field-label" htmlFor="scan">
          Scan barcode or search by title
        </label>
        <input
          id="scan"
          ref={searchRef}
          className="input input-scan"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onSearchKeyDown}
          placeholder="Scan a barcode, or type a title / ISBN / SKU"
          autoComplete="off"
          spellCheck={false}
          // The list is walked from here rather than tabbed into, so the box
          // keeps the focus a scan needs while the arrows move a highlight
          // inside it. `aria-activedescendant` is what carries that highlight
          // to a screen reader, since the focus itself never moves.
          role="combobox"
          aria-expanded={results.length > 0}
          aria-controls="scan-results"
          aria-autocomplete="list"
          aria-activedescendant={
            activeIndex >= 0 ? `scan-result-${activeIndex}` : undefined
          }
        />

        <div className="results">
          {/* Its own region, and always present so a message that appears
              later is actually announced. Empty, it collapses to nothing. */}
          <div className="results-status" aria-live="polite">
            {searching && <p className="muted">Searching…</p>}
            {searchError && <p className="error">{searchError}</p>}
            {!searching && !searchError && query.trim().length >= 2 && results.length === 0 && (
              <p className="muted">No books matched “{query.trim()}”.</p>
            )}
          </div>

          <div
            id="scan-results"
            className="results-list"
            role="listbox"
            aria-label="Search results"
          >
            {results.map((book, i) => {
              const soldOut = !isSellable(book);
              const active = i === activeIndex;
              return (
                <button
                  key={book.id}
                  id={`scan-result-${i}`}
                  type="button"
                  role="option"
                  aria-selected={active}
                  className={
                    soldOut
                      ? 'result result-out'
                      : active
                        ? 'result result-active'
                        : 'result'
                  }
                  onClick={() => addBook(book)}
                  disabled={soldOut}
                  // `disabled` alone is not carried by the option role, so a
                  // sold-out row would read as billable. The arrow keys skip
                  // these anyway; this is for anyone reading the list.
                  aria-disabled={soldOut}
                >
                  <span className="result-main">
                    <span className="result-title">
                      {/* The clean copy of the same title is a separate row at
                          a higher price, often right above or below this one.
                          The tag is what stops the wrong one being rung up. */}
                      <span className="title-text" title={book.title}>
                        {book.title}
                      </span>
                      {book.isDCopy ? (
                        <span className="tag tag-dcopy">D-COPY</span>
                      ) : null}
                      {book.isUsed ? (
                        <span className="tag tag-used">USED</span>
                      ) : null}
                      {/* The same title at the publisher's old and new MRP:
                          the cover in hand says which. */}
                      {repricedMrpOf(book, results) !== null ? (
                        <span className="tag tag-mrp">
                          MRP {money(repricedMrpOf(book, results))}
                        </span>
                      ) : null}
                    </span>
                    <span className="result-code">{book.sku ?? book.isbn13 ?? ''}</span>
                  </span>
                  <span className="result-right">
                    <span className="result-price">{money(book.price)}</span>
                    <span className={soldOut ? 'stock stock-out' : 'stock'}>
                      {stockLabelOf(book)}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      <section className="pane pane-bill">
        <div className="bill-head">
          <h2>Current bill</h2>
          {lines.length > 0 && (
            <button type="button" className="link-btn" onClick={clearBill}>
              Clear
            </button>
          )}
        </div>

        {lastSale && (
          <div className="banner banner-ok" role="status">
            <strong>Bill {billNumberOf(lastSale)} completed</strong>
            <span>{money(saleTotalOf(lastSale))} — ready for the next customer.</span>
            {/* The dialog gets closed by mistake often enough that the way back
                to it has to be here, not three screens away. */}
            <button type="button" className="link-btn" onClick={() => openInvoice(lastSale)}>
              Print or save this bill
            </button>
          </div>
        )}

        {lines.length === 0 ? (
          <p className="muted empty">Scan a book to start a bill.</p>
        ) : (
          <ul className="lines">
            {lines.map(({ book, quantity }) => (
              <li key={book.id} className="line">
                <div className="line-info">
                  <span className="line-title">
                    {/* The column is too narrow for a long title, and the
                        ellipsis can fall before the part that tells two
                        editions apart. Hovering the name gives the whole of
                        it back without widening the line or wrapping it. */}
                    <span className="title-text" title={book.title}>
                      {book.title}
                    </span>
                    {book.isDCopy ? (
                      <span className="tag tag-dcopy">D-COPY</span>
                    ) : null}
                    {book.isUsed ? (
                      <span className="tag tag-used">USED</span>
                    ) : null}
                  </span>
                  <span className="line-unit">{money(book.price)} each</span>
                </div>
                <div className="qty">
                  <button
                    type="button"
                    className="qty-btn"
                    onClick={() => setQuantity(book.id, quantity - 1)}
                    aria-label={`Reduce quantity of ${book.title}`}
                  >
                    −
                  </button>
                  <input
                    className="qty-input"
                    inputMode="numeric"
                    value={quantity}
                    onChange={(e) => setQuantity(book.id, Number(e.target.value.replace(/\D/g, '') || 0))}
                    aria-label={`Quantity of ${book.title}`}
                  />
                  <button
                    type="button"
                    className="qty-btn"
                    onClick={() => setQuantity(book.id, quantity + 1)}
                    disabled={quantity >= (stockCountOf(book) ?? Number.POSITIVE_INFINITY)}
                    aria-label={`Increase quantity of ${book.title}`}
                  >
                    +
                  </button>
                </div>
                <span className="line-total">{money(Number(book.price) * quantity)}</span>
                <button
                  type="button"
                  className="remove"
                  onClick={() => removeLine(book.id)}
                  aria-label={`Remove ${book.title}`}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        )}

        {/* One row on an ordinary bill, three when something has been taken
            off it — because on a discounted bill "what the books came to",
            "what was knocked off" and "what to ask for" are three different
            numbers, and staff read the last one out loud. */}
        <div className="totals">
          {isTransfer ? (
            <>
              <div className="totals-row totals-sub">
                <span>{itemCount} item{itemCount === 1 ? '' : 's'}</span>
                <span>{money(subtotal)}</span>
              </div>
              {/* An em dash, not ₹0.00 — nothing is being asked for, and a
                  zero here is the figure staff would read out loud. */}
              <div className="totals-row">
                <span>To pay</span>
                <strong>—</strong>
              </div>
            </>
          ) : totalOff > 0 ? (
            <>
              <div className="totals-row totals-sub">
                <span>{itemCount} item{itemCount === 1 ? '' : 's'}</span>
                <span>{money(subtotal)}</span>
              </div>
              {couponDiscount > 0 && (
                <div className="totals-row totals-sub">
                  <span>Coupon {couponAnswer?.result?.code}</span>
                  <span>− {money(couponDiscount)}</span>
                </div>
              )}
              {discountApplied > 0 && (
                <div className="totals-row totals-sub">
                  <span>Discount</span>
                  <span>− {money(discountApplied)}</span>
                </div>
              )}
              <div className="totals-row">
                <span>To pay</span>
                <strong>{money(total)}</strong>
              </div>
            </>
          ) : (
            <div className="totals-row">
              <span>{itemCount} item{itemCount === 1 ? '' : 's'}</span>
              <strong>{money(total)}</strong>
            </div>
          )}
        </div>

        {!isTransfer && (
        <CustomerDetails
          value={customer}
          onChange={(next) => {
            setCustomer(next);
            // Ticking the box disables the field staff were standing in,
            // which would leave nothing focused for the next scan to land in.
            if (next.declined && !customer.declined) focusSearch();
          }}
          open={customerOpen}
          onToggle={() => setCustomerOpen((v) => !v)}
          problem={customerAsked ? customerProblem(customer) : null}
          phoneRef={customerPhoneRef}
          nameRef={customerNameRef}
        />
        )}

        {/* Sits above the tender because it decides whether there is one.
            Ticking it takes the money off the bill entirely: no payment, no
            discount, nothing in the day's takings — only the stock moves. */}
        <div className="transfer">
          <label className="transfer-label">
            <input
              type="checkbox"
              checked={isTransfer}
              onChange={(e) => {
                setIsTransfer(e.target.checked);
                // Whatever was half-typed for a customer goes with it. It
                // would not be sent anyway, but leaving a name sitting in a
                // collapsed panel means it reappears on the next bill, which
                // is somebody else's.
                if (e.target.checked) {
                  setCouponText('');
                  setCouponCode(null);
                  setCouponCheck(null);
                  setCustomer(EMPTY_CUSTOMER);
                  setCustomerOpen(false);
                  setCustomerAsked(false);
                  setSaleError(null);
                }
              }}
            />
            <span>Shop transfer — moving these books to the other shop</span>
          </label>
          {isTransfer && (
            <p className="muted small">
              No payment is taken. The copies come off the shelf and are listed
              separately at the foot of the sales report, with no amount.
            </p>
          )}
        </div>

        {!isTransfer && (
        <>
        <TenderPicker
          idPrefix="bill-tender"
          value={tender}
          total={total}
          onChange={(next) => {
            setTender(next);
            setSaleError(null);
          }}
          showProblem={tenderAsked}
        />
        {(tender.split ? tender.amounts.CARD || tender.amounts.UPI : tender.method !== 'CASH') && (
          <p className="muted small">
            Take the card and UPI payments on the machine first, then complete the bill.
          </p>
        )}

        {/* The customer's coupon, before the counter's own discount because
            it is taken off first. */}
        <div className="discount coupon">
          <label className="field-label" htmlFor="bill-coupon">
            Coupon code
          </label>
          <div className="discount-row">
            <input
              id="bill-coupon"
              className="input coupon-input"
              value={couponText}
              onChange={(e) => {
                setCouponText(e.target.value.replace(/\s/g, '').toUpperCase());
                setSaleError(null);
                // Typing over an applied code takes it off until Apply is
                // pressed again, so the bill never shows one code and
                // charges another.
                if (couponCode) {
                  setCouponCode(null);
                  setCouponCheck(null);
                }
              }}
              onKeyDown={(e) => {
                if (e.key !== 'Enter') return;
                e.preventDefault();
                if (couponCode) focusSearch();
                else applyCoupon();
              }}
              placeholder="e.g. DIWALI50"
              autoComplete="off"
              maxLength={50}
              disabled={!lines.length}
              aria-describedby="bill-coupon-note"
            />
            {couponCode ? (
              <button type="button" className="link-btn" onClick={removeCoupon}>
                Remove
              </button>
            ) : (
              <button
                type="button"
                className="btn"
                onClick={applyCoupon}
                disabled={!lines.length || !couponText.trim()}
              >
                Apply
              </button>
            )}
          </div>
          {couponCode && (
            <p
              id="bill-coupon-note"
              className={couponProblem ? 'error small' : 'muted small'}
              role={couponProblem ? 'alert' : undefined}
            >
              {couponProblem ??
                (couponPending
                  ? 'Checking the coupon…'
                  : `${couponAnswer?.result?.code} — ${money(couponDiscount)} off` +
                    (couponAnswer?.result?.description
                      ? ` · ${couponAnswer.result.description}`
                      : ''))}
            </p>
          )}
        </div>

        {/* Last thing before the bill is completed, and deliberately so: the
            discount is what the customer and the counter settle on after the
            books are all scanned and the figure has been read out. Putting it
            up beside the search box would invite it to be typed first, against
            a bill that is still being built. */}
        <div className="discount">
          <label className="field-label" htmlFor="bill-discount">
            Discount
          </label>
          <div className="discount-row discount-percents">
            {DISCOUNT_PERCENTS.map((pct) => (
              <button
                key={pct}
                type="button"
                className={`btn discount-percent${discountPercent === pct ? ' active' : ''}`}
                aria-pressed={discountPercent === pct}
                disabled={!lines.length}
                onClick={() => {
                  // A second press on the picked one takes it off again.
                  setDiscountPercent((prev) => (prev === pct ? null : pct));
                  setDiscountText('');
                  setSaleError(null);
                  focusSearch();
                }}
              >
                {pct}% off
              </button>
            ))}
          </div>
          <div className="discount-row">
            <span className="discount-rupee" aria-hidden="true">
              ₹
            </span>
            <input
              id="bill-discount"
              className="input discount-input"
              inputMode="decimal"
              value={discountText}
              // Digits and at most one point. The scanner types into whatever
              // holds focus, so a stray scan landing in here must not be able
              // to turn a barcode into a discount — every non-digit is
              // dropped, and the amount is capped at the bill above.
              onChange={(e) => {
                const cleaned = e.target.value
                  .replace(/[^\d.]/g, '')
                  .replace(/(\..*)\./g, '$1')
                  .replace(/^(\d*\.\d{0,2}).*$/, '$1');
                setSaleError(null);
                // Rupees typed in replace a percentage picked above.
                setDiscountPercent(null);
                setDiscountText(cleaned);
              }}
              // A scanner types its code and then sends Enter. Enter here hands
              // focus straight back to the search box, so the scan AFTER a
              // discount has been typed goes onto the bill rather than into
              // this box behind it.
              onKeyDown={(e) => {
                if (e.key !== 'Enter') return;
                e.preventDefault();
                focusSearch();
              }}
              placeholder="0"
              autoComplete="off"
              disabled={!lines.length}
              aria-describedby="bill-discount-note"
            />
            {(discountText !== '' || discountPercent !== null) && (
              <button
                type="button"
                className="link-btn"
                onClick={() => {
                  setDiscountText('');
                  setDiscountPercent(null);
                  setSaleError(null);
                  focusSearch();
                }}
              >
                Remove
              </button>
            )}
          </div>
          <p
            id="bill-discount-note"
            className={discountProblem ? 'error small' : 'muted small'}
            role={discountProblem ? 'alert' : undefined}
          >
            {discountProblem ??
              (discountPercent !== null
                ? couponDiscount > 0
                  ? `${discountPercent}% off what is left after the coupon — ${money(discountApplied)}.`
                  : `${discountPercent}% off the whole bill — ${money(discountApplied)}.`
                : couponDiscount > 0
                  ? 'Rupees off on top of the coupon, or pick 5% or 10%. Leave blank if there is none.'
                  : 'Rupees off the whole bill, or pick 5% or 10%. Leave blank if there is none.')}
          </p>
        </div>
        </>
        )}

        {saleError && (
          <p className="error error-block" role="alert">
            {saleError}
          </p>
        )}

        <button
          type="button"
          className="btn btn-primary btn-complete"
          onClick={askToComplete}
          disabled={
            !lines.length || saving || confirming || !!discountProblem || couponPending
          }
        >
          {saving
            ? 'Completing…'
            : isTransfer
              ? `Complete transfer · ${itemCount} item${itemCount === 1 ? '' : 's'}`
              : `Complete sale · ${money(total)}`}
        </button>
      </section>

      {confirming && (
        <ConfirmSale
          lines={lines}
          subtotal={subtotal}
          discount={discountApplied}
          couponCode={couponAnswer?.result?.code}
          couponDiscount={couponDiscount}
          total={total}
          itemCount={itemCount}
          paymentText={tenderLabel(tender)}
          isTransfer={isTransfer}
          customer={customer}
          saving={saving}
          onCancel={closeConfirm}
          onConfirm={() => void completeSale()}
        />
      )}

      {invoice && <InvoiceView sale={invoice} onClose={closeInvoice} />}
    </div>
  );
}
